import { Reveal } from "../components/Reveal";
import { SectionLabel } from "../components/SectionLabel";

const CARDS = [
  {
    id: "show",
    n: "01",
    title: "SHOW",
    tagline: "Own the room.",
    copy: "Custom trade-show booths, convention builds and brand activations made to turn traffic into conversations.",
    tags: ["Booths", "Backdrops", "Activations", "Events"],
    accent: "acid",
  },
  {
    id: "shelf",
    n: "02",
    title: "SHELF",
    tagline: "Win at retail.",
    copy: "Product displays, cases, counters and permanent installations that keep your brand working after the show ends.",
    tags: ["Displays", "Cases", "Counters", "Retail"],
    accent: "uv",
  },
  {
    id: "space",
    n: "03",
    title: "SPACE",
    tagline: "Change the environment.",
    copy: "Color-critical wall, window and floor graphics paired with dimensional signage and custom architectural elements.",
    tags: ["Print", "Walls", "Signage", "Interiors"],
    accent: "uv",
  },
  {
    id: "object",
    n: "04",
    title: "OBJECT",
    tagline: "Make the impossible real.",
    copy: "Oversized products, illuminated logos and sculptural props engineered to become the photograph everyone takes.",
    tags: ["3D Print", "CNC", "Sculpture", "Lighting"],
    accent: "acid",
  },
];

const THEME = {
  acid: {
    fill: "bg-acid",
    title: "group-hover:text-ink",
    body: "group-hover:text-ink/70",
    num: "group-hover:text-ink/50",
    tag: "group-hover:border-ink/40 group-hover:text-ink",
  },
  uv: {
    fill: "bg-uv",
    title: "group-hover:text-paper",
    body: "group-hover:text-paper/85",
    num: "group-hover:text-paper/60",
    tag: "group-hover:border-paper/40 group-hover:text-paper",
  },
};

export default function Capabilities() {
  return (
    <section
      id="capabilities"
      data-testid="capabilities-section"
      className="scroll-mt-20 bg-ink"
    >
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-36">
        <Reveal>
          <SectionLabel index="CH.03">Capabilities</SectionLabel>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.12] tracking-tight text-paper sm:text-5xl sm:leading-[1.08] lg:text-6xl">
              Show. Shelf.
              <br />
              Space. Object<span className="text-acid">.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="text-base leading-relaxed text-fog sm:text-lg">
              Not a catalog of standard packages. A flexible production system for launches, retail
              programs, conventions and the ideas other vendors say are too difficult.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          {CARDS.map((c, i) => {
            const t = THEME[c.accent];
            return (
              <Reveal key={c.id} delay={0.08 * i} y={36}>
                <article
                  data-testid={`capability-card-${c.id}`}
                  className="group relative flex min-h-[22rem] flex-col justify-between overflow-hidden border border-white/10 bg-panel p-8 transition-colors duration-500 lg:p-10"
                >
                  <div
                    className={`absolute inset-0 translate-y-full transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-y-0 ${t.fill}`}
                    aria-hidden="true"
                  />
                  <div className="relative z-10 flex items-start justify-between">
                    <span
                      className={`font-mono text-xs tracking-[0.3em] text-acid transition-colors duration-500 ${t.num}`}
                    >
                      {c.n}
                    </span>
                    <span
                      className={`font-display text-5xl font-extrabold uppercase leading-none text-paper/10 transition-colors duration-500 ${t.num}`}
                      aria-hidden="true"
                    >
                      {c.title.slice(0, 1)}
                    </span>
                  </div>
                  <div className="relative z-10">
                    <h3
                      className={`font-display text-3xl font-extrabold uppercase tracking-tight text-paper transition-colors duration-500 sm:text-4xl ${t.title}`}
                    >
                      {c.title}
                    </h3>
                    <p
                      className={`mt-2 font-display text-lg font-bold text-acid transition-colors duration-500 ${t.title}`}
                    >
                      {c.tagline}
                    </p>
                    <p
                      className={`mt-4 max-w-md text-sm leading-relaxed text-fog transition-colors duration-500 sm:text-base ${t.body}`}
                    >
                      {c.copy}
                    </p>
                    <ul className="mt-6 flex flex-wrap gap-2">
                      {c.tags.map((tag) => (
                        <li
                          key={tag}
                          className={`border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper/70 transition-colors duration-500 ${t.tag}`}
                        >
                          {tag}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
